import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, Clock, User, Calendar, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { DevOpsTask, DevOpsTaskStatus, DevOpsTaskPriority } from '../../types/devopsTasks';
import { User as UserType } from '../../types';

interface MyTasksProps {
  currentUser: UserType;
  onSelectTask: (taskId: string) => void;
  onBack?: () => void;
}

const MyTasks: React.FC<MyTasksProps> = ({ currentUser, onSelectTask, onBack }) => {
  const [tasks, setTasks] = useState<DevOpsTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchMyTasks();
  }, [currentUser.id]);

  const fetchMyTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/tasks?createdBy=${currentUser.id}`);
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();
      setTasks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  // Filter tasks by search
  const filteredTasks = tasks.filter(task => 
    searchQuery === '' || 
    task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    task.taskNumber.toString().toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group tasks by status
  const openTasks = filteredTasks.filter(t => ['new', 'assigned', 'in-progress'].includes(t.status));
  const testingTasks = filteredTasks.filter(t => t.status === 'testing');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');
  const rejectedTasks = filteredTasks.filter(t => t.status === 'rejected');

  // Priority badge colors
  const getPriorityColor = (priority: DevOpsTaskPriority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  // Status badge colors
  const getStatusColor = (status: DevOpsTaskStatus) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'assigned': return 'bg-indigo-100 text-indigo-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'testing': return 'bg-cyan-100 text-cyan-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Task card component
  const TaskCard: React.FC<{ task: DevOpsTask }> = ({ task }) => (
    <div
      onClick={() => task._id && onSelectTask(task._id)}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer group"
    >
      <div className="space-y-4">
        {/* Header with Task Number and Priority */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg font-mono">
            #{task.taskNumber}
          </span>
          <span className={`px-3 py-1 text-xs font-bold rounded-full border shadow-sm ${getPriorityColor(task.priority)}`}>
            {task.priority.toUpperCase()}
          </span>
        </div>

        {/* Title and Description */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-1 group-hover:text-blue-600 transition-colors">
            {task.title}
          </h3>
          <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">{task.description}</p>
        </div>

        {/* Status Badge */}
        <div className="flex items-center">
          <span className={`px-4 py-1.5 text-xs font-bold rounded-full shadow-sm ${getStatusColor(task.status)}`}>
            {task.status.replace('-', ' ').toUpperCase()}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100"></div>

        {/* Task Metadata */}
        <div className="space-y-2.5 text-sm">
          {task.assignedTo && (
            <div className="flex items-center justify-between">
              <div className="flex items-center text-gray-500">
                <User className="w-4 h-4 mr-2" />
                <span className="font-medium">Assigned to</span>
              </div>
              <span className="text-gray-900 font-semibold truncate ml-2">{task.assignedTo.name}</span>
            </div>
          )}

          {task.expectedCompletionDate && (
            <div className="flex items-center justify-between">
              <div className="flex items-center text-gray-500">
                <Calendar className="w-4 h-4 mr-2" />
                <span className="font-medium">Due Date</span>
              </div>
              <span className="text-gray-900 font-semibold">{formatDate(task.expectedCompletionDate)}</span>
            </div>
          )}

          <div className="flex items-center justify-between text-gray-500">
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              <span className="font-medium">Created</span>
            </div>
            <span className="font-semibold">{formatDate(task.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
        <span className="text-red-800">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6">
        <h1 className="text-3xl font-bold text-white">My Tasks</h1>
        <p className="text-blue-100 mt-2 text-lg">Tasks created by you</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-700 mb-1">Total Created</p>
              <p className="text-3xl font-bold text-blue-900">{tasks.length}</p>
            </div>
            <div className="w-14 h-14 bg-blue-200 rounded-xl flex items-center justify-center shadow-sm">
              <Clock className="w-7 h-7 text-blue-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-sm border border-orange-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-orange-700 mb-1">Open</p>
              <p className="text-3xl font-bold text-orange-900">{openTasks.length}</p>
            </div>
            <div className="w-14 h-14 bg-orange-200 rounded-xl flex items-center justify-center shadow-sm">
              <AlertCircle className="w-7 h-7 text-orange-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-green-700 mb-1">Completed</p>
              <p className="text-3xl font-bold text-green-900">{completedTasks.length}</p>
            </div>
            <div className="w-14 h-14 bg-green-200 rounded-xl flex items-center justify-center shadow-sm">
              <CheckCircle className="w-7 h-7 text-green-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-sm border border-red-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-red-700 mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-900">{rejectedTasks.length}</p>
            </div>
            <div className="w-14 h-14 bg-red-200 rounded-xl flex items-center justify-center shadow-sm">
              <XCircle className="w-7 h-7 text-red-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search your tasks by title, description, or task number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm placeholder-gray-400"
          />
        </div>
      </div>

      {/* Task Groups */}
      {filteredTasks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No tasks found</h3>
          <p className="text-gray-600 text-lg">
            {searchQuery ? 'Try adjusting your search criteria' : "You haven't created any tasks yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Open Tasks */}
          {openTasks.length > 0 && (
            <div>
              <div className="flex items-center mb-4 pb-2 border-b-2 border-orange-200">
                <AlertCircle className="w-6 h-6 mr-3 text-orange-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  Open Tasks 
                  <span className="ml-3 px-3 py-1 text-sm font-bold bg-orange-100 text-orange-700 rounded-full">
                    {openTasks.length}
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {openTasks.map((task) => (
                  <TaskCard key={task._id} task={task} />
                ))}
              </div>
            </div>
          )}

          {/* Testing Tasks */}
          {testingTasks.length > 0 && (
            <div>
              <div className="flex items-center mb-4 pb-2 border-b-2 border-cyan-200">
                <Clock className="w-6 h-6 mr-3 text-cyan-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  In Testing 
                  <span className="ml-3 px-3 py-1 text-sm font-bold bg-cyan-100 text-cyan-700 rounded-full">
                    {testingTasks.length}
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {testingTasks.map((task) => (
                  <TaskCard key={task._id} task={task} />
                ))}
              </div>
            </div>
          )}

          {/* Completed Tasks */}
          {completedTasks.length > 0 && (
            <div>
              <div className="flex items-center mb-4 pb-2 border-b-2 border-green-200">
                <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  Completed 
                  <span className="ml-3 px-3 py-1 text-sm font-bold bg-green-100 text-green-700 rounded-full">
                    {completedTasks.length}
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {completedTasks.map((task) => (
                  <TaskCard key={task._id} task={task} />
                ))}
              </div>
            </div>
          )}

          {/* Rejected Tasks */}
          {rejectedTasks.length > 0 && (
            <div>
              <div className="flex items-center mb-4 pb-2 border-b-2 border-red-200">
                <XCircle className="w-6 h-6 mr-3 text-red-600" />
                <h2 className="text-xl font-bold text-gray-900">
                  Rejected 
                  <span className="ml-3 px-3 py-1 text-sm font-bold bg-red-100 text-red-700 rounded-full">
                    {rejectedTasks.length}
                  </span>
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                {rejectedTasks.map((task) => (
                  <TaskCard key={task._id} task={task} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyTasks;
